import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import {
    getIsCartInFlightRequests,
    useCartOptimisticUpdateCounterActions,
} from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { cartItemRemoveMutation } from '@/features/pos/components/Cart/mutations'
import { CartItemWithIndex } from '@/features/pos/components/Cart/types'
import {
    getCartFromCache,
    writeCartItemsToCache,
} from '@/features/pos/components/Cart/utils/cart-cache'
import {
    findCartItemWithIndex,
    insertCartItem,
    removeCartItem,
} from '@/features/pos/components/Cart/utils/cart-item'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useApolloClient, useMutation } from '@apollo/client'
import { useCallback, useRef } from 'react'
import invariant from 'tiny-invariant'

type RemovedCartItems = Record<string, CartItemWithIndex>
type ClientOptions = { variables?: { itemUuid?: string } }

export const useRemoveCartItem = (): ((id: string) => Promise<void>) => {
    const client = useApolloClient()

    const removedCartItemsRef = useRef<RemovedCartItems>({})

    const { increment, lock, reset } = useCartOptimisticUpdateCounterActions()

    const { cartKey } = useCartKeyContext()

    const withRemovedCartItem = (
        clientOptions: ClientOptions | undefined,
        callback: (carteItemWithIndex: CartItemWithIndex) => void
    ) => {
        const cartItemId = clientOptions?.variables?.itemUuid
        invariant(cartItemId, "'itemUuid' variable must be set")

        const cartItemWithIndex = removedCartItemsRef.current[cartItemId]
        invariant(cartItemWithIndex, "Cart item must be set in 'removedCartItemsRef'")

        callback(cartItemWithIndex)

        reset(cartItemId)
        delete removedCartItemsRef.current[cartItemId]
    }

    const handleError = useHandleErrorWithToast(logger)

    const [cartItemRemove] = useMutation(cartItemRemoveMutation, {
        fetchPolicy: 'no-cache',
        onCompleted: (data, clientOptions) => {
            withRemovedCartItem(clientOptions, () => {
                if (getIsCartInFlightRequests() || !data.cartItemRemove) return

                writeCartItemsToCache(client.cache, cartKey!, data.cartItemRemove.items)
            })
        },
        onError: (error, clientOptions) => {
            handleError(error)

            withRemovedCartItem(clientOptions, (cartItemWithIndex) => {
                const currentCart = getCartFromCache(client.cache, cartKey!)

                writeCartItemsToCache(
                    client.cache,
                    cartKey!,
                    insertCartItem(
                        currentCart?.items,
                        cartItemWithIndex.item,
                        cartItemWithIndex.index
                    )
                )
            })
        }, // needed to prevent app crash when error happened
    })

    return useCallback(
        async (id: string) => {
            invariant(cartKey, "'cartKey' must be set")

            increment(id)
            lock(id)

            const currentCart = getCartFromCache(client.cache, cartKey)

            removedCartItemsRef.current[id] = findCartItemWithIndex(currentCart?.items, id)!

            writeCartItemsToCache(client.cache, cartKey, removeCartItem(currentCart?.items, id))

            await cartItemRemove({ variables: { cartKey, itemUuid: id } })
        },
        [cartKey, cartItemRemove, client, increment, lock]
    )
}
